"use client";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { shouldEnablePrivacyForResourceType } from "@gettactic/helpers/src/resources/utils";
import {
  type IResourceWithSchedules,
  useAuthenticated,
  useSettings
} from "@gettactic/hooks";
import {
  Armchair,
  Car,
  LayoutGrid,
  type LucideIcon,
  Users
} from "lucide-react";
import React from "react";
import { ResourceHoverStatusLine } from "@/features/map/components/ResourceHoverStatusLine";
import { getResourceHoverStatus } from "@/features/map/helpers/resourceHoverStatus";
import {
  isResourceOutOfService,
  withVisibleAssignments
} from "@/features/map/helpers/resourceStatus";
import { useMap } from "@/features/map/MapProvider";

const NO_OFFICE_RESOURCES: readonly IResource[] = [];

/** Resource-type glyph shown when a resource has no uploaded photo. */
const glyphForType = (type: IResource["type"]): LucideIcon => {
  switch (type) {
    case "meeting_room":
      return Users;
    case "parking_lot":
    case "parking_space":
    case "vehicle":
    case "bike":
      return Car;
    case "desk":
    case "workspace":
    case "booth":
      return Armchair;
    default:
      return LayoutGrid;
  }
};

const ResourceThumbnail: React.FC<{ resource: IResource }> = ({ resource }) => {
  // Show whatever image getResourceCoverUrl resolves (custom upload or the
  // themed default for desks/rooms); only fall back to the glyph when the
  // resource genuinely has no image.
  const coverUrl = getResourceCoverUrl(resource, 96);

  if (coverUrl) {
    return (
      // biome-ignore lint/performance/noImgElement: small thumbnail, avoids next/image host config
      <img
        src={coverUrl}
        alt=""
        className="h-[45px] w-[45px] shrink-0 rounded-[10px] object-cover"
      />
    );
  }

  const Glyph = glyphForType(resource.type);
  return (
    <div className="flex h-[45px] w-[45px] shrink-0 items-center justify-center rounded-[10px] bg-neutral-100 text-neutral-400">
      <Glyph className="h-5 w-5" aria-hidden="true" />
    </div>
  );
};

/**
 * Minimal read-only hover preview: thumbnail + name + location + one status
 * line. No amenities, schedules or actions — the click popover owns those.
 */
export const ResourceHoverPreview: React.FC<{
  resource: IResourceWithSchedules;
  /**
   * Reference time for occupancy — the map's searched date/time, not the
   * literal wall clock. The map's avatar (InteractiveZones) is derived from
   * the same searched window, so using anything else here (e.g. `new Date()`)
   * makes the popover disagree with the avatar whenever the search date/time
   * isn't the current instant.
   */
  now: Date;
}> = ({ resource, now }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { isOfficePrivacyEnabled } = useSettings();

  // Mirror SchedulesGroups: hide identities when org privacy is on for this
  // resource type and the current user isn't part of the reservation.
  const hideIdentity = React.useMemo(() => {
    if (
      !isOfficePrivacyEnabled(resource.office_id) ||
      !shouldEnablePrivacyForResourceType(resource.type)
    ) {
      return false;
    }
    const isCurrentUserInGroup = (resource.schedules ?? []).some((schedule) =>
      schedule.slots.some(
        (slot) => slot.user.id === authenticatedUser?.user?.id
      )
    );
    return !isCurrentUserInGroup;
  }, [
    resource.office_id,
    resource.type,
    resource.schedules,
    isOfficePrivacyEnabled,
    authenticatedUser?.user?.id
  ]);

  // The hovered resource may come from the schedulable payload, which carries
  // no owner or visible assignments and zeroes `is_available` for anything
  // the viewer can't book. The unfiltered office resources list supplies the
  // owner the released-desk note names, the assignees, and the raw
  // "switched off" toggle (see isResourceOutOfService).
  const { allResources: officeResources = NO_OFFICE_RESOURCES } = useMap();
  const resourceWithOwner = React.useMemo(
    () => withVisibleAssignments(resource, officeResources),
    [resource, officeResources]
  );
  const status = getResourceHoverStatus(resourceWithOwner, {
    now,
    hideIdentity,
    isOutOfService: isResourceOutOfService(resource, officeResources)
  });

  return (
    <div className="flex items-center gap-3">
      <ResourceThumbnail resource={resource} />
      <ResourceLabel
        className={cn("min-w-0 flex-1")}
        resource={resource}
        showPicture={false}
        showAmenities={false}
        allowFavorite={false}
        headingClassName="font-inter font-semibold text-[14.5px] text-neutral-900 leading-[17.4px]"
        hierarchyClassName="font-inter text-[11.5px] text-neutral-700"
        infoWrapperClassName="gap-0"
        footer={
          <div className="pt-1">
            <ResourceHoverStatusLine status={status} />
          </div>
        }
      />
    </div>
  );
};
